import { retrieveRawInitData } from '@telegram-apps/sdk-react';
import { isTelegram } from '../lib/telegram';

/**
 * Заголовки авторизации. Внутри Telegram — настоящая initData, подписанная
 * токеном бота; в обычном браузере — dev-пользователь, которого сервер примет
 * только при DEV_AUTH=1.
 *
 * initData нигде не кэшируется и не кладётся в localStorage: она короткоживущая,
 * и оставлять её на диске устройства незачем.
 */

export const inTelegram = isTelegram;

/**
 * Пользователь, которым фронт представляется без Telegram. Только в dev-сборке.
 *
 * Имя в заголовке кириллическое, а значения HTTP-заголовков — латиница: без
 * экранирования fetch падает TypeError, и ошибка выглядит как «нет сети».
 * Сервер разбирает заголовок через JSON.parse, для которого \uXXXX — норма.
 */
export function devUser(): string | null {
  if (!import.meta.env.DEV) return null;
  const raw = readDevOverride() ?? import.meta.env.VITE_DEV_USER;
  if (!raw || !raw.trim()) return null;
  return asciiOnly(raw.trim());
}

const DEV_USER_KEY = 'dev-user';

/**
 * Переключатель ролей в dev-сборке кладёт выбранного пользователя в
 * sessionStorage: `.env` ради проверки другой роли перезаписывать не хочется.
 * initData так не хранится и храниться не должна — это лишь имя, которым фронт
 * представляется серверу с DEV_AUTH=1.
 */
function readDevOverride(): string | null {
  if (!import.meta.env.DEV) return null;
  try {
    return sessionStorage.getItem(DEV_USER_KEY);
  } catch {
    return null;
  }
}

export function setDevUser(value: string | null): void {
  if (!import.meta.env.DEV) return;
  try {
    if (value) sessionStorage.setItem(DEV_USER_KEY, value);
    else sessionStorage.removeItem(DEV_USER_KEY);
  } catch {
    /* приватный режим браузера — переключатель просто не запомнит выбор */
  }
}

export function currentDevUserId(): number | null {
  const raw = devUser();
  if (!raw) return null;
  try {
    return (JSON.parse(raw) as { id?: number }).id ?? null;
  } catch {
    return null;
  }
}

function asciiOnly(value: string): string {
  return value.replace(/[^\x20-\x7e]/g, (ch) => '\\u' + ch.charCodeAt(0).toString(16).padStart(4, '0'));
}

/**
 * Сырая initData, годная для заголовка. Мусор отсекается здесь: значение с
 * переводом строки роняет сам `fetch`, и ошибка выглядит как «нет сети».
 */
function rawInitData(): string | null {
  if (!inTelegram()) return null;
  try {
    const raw = retrieveRawInitData();
    if (!raw || /[^\t\x20-\x7e]/.test(raw)) return null;
    return raw;
  } catch {
    return null;
  }
}

export function isDevAuth(): boolean {
  return rawInitData() === null && devUser() !== null;
}

const OWNER_TENANT_KEY = 'owner-tenant';

/**
 * Организация, в которую вошёл владелец продукта. Хранится на вкладке: вход в
 * чужую организацию — это визит, а не смена аккаунта, и переживать перезапуск
 * приложения он не должен. Сервер всё равно проверяет права на каждом запросе:
 * подставить сюда чужой идентификатор — не значит получить доступ.
 */
export function ownerTenant(): string | null {
  try {
    return sessionStorage.getItem(OWNER_TENANT_KEY);
  } catch {
    return null;
  }
}

export function setOwnerTenant(id: string | null): void {
  try {
    if (id) sessionStorage.setItem(OWNER_TENANT_KEY, id);
    else sessionStorage.removeItem(OWNER_TENANT_KEY);
  } catch {
    /* приватный режим браузера — визит просто не запомнится */
  }
}

export function authHeaders(): Record<string, string> {
  const headers: Record<string, string> = {};

  // Организация определяется ДО проверки подписи. На бою хватает поддомена,
  // локально нужен явный X-Tenant. Визит владельца перевешивает и то, и другое.
  const tenant = ownerTenant() ?? import.meta.env.VITE_TENANT;
  if (tenant) headers['X-Tenant'] = tenant;

  const raw = rawInitData();
  if (raw) {
    headers.Authorization = `tma ${raw}`;
    return headers;
  }

  // Ветка целиком вырезается из боевой сборки: в dist не остаётся даже имени заголовка.
  if (import.meta.env.DEV) {
    const dev = devUser();
    if (dev) headers['X-Dev-User'] = dev;
  }
  return headers;
}
